import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  useGetBlogPostsQuery,
  useUpdateBlogPostMutation,
} from "../../features/api/blogApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import "./blogAdmin.scss";
import { useToast } from "../toast/ToastContext";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "blockquote"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

export default function AdminBlogEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Fetch all posts using admin mode to extract our target document from the collection cache
  const { data } = useGetBlogPostsQuery({
    page: 1,
    limit: 100,
    adminMode: true,
  });
  const [updateBlogPost, { isLoading: isSubmitting }] =
    useUpdateBlogPostMutation();

  const currentPost = data?.items?.find((item) => item._id === id);

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Road Trips");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Safely initialize state variables the moment the cache returns the target post details
  useEffect(() => {
    if (currentPost) {
      setTitle(currentPost.title || "");
      setCategory(currentPost.category || "Road Trips");
      setExcerpt(currentPost.excerpt || "");
      setContent(currentPost.content || "");
      setIsPublished(currentPost.isPublished || false);
      setPreviewUrl(currentPost.coverImage || "");
    }
  }, [currentPost]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(false);

    if (
      !title.trim() ||
      !excerpt.trim() ||
      !content.trim() ||
      content === "<p><br></p>"
    ) {
      setError("Please fill out all required text parameters.");
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("category", category);
    formData.append("excerpt", excerpt.trim());
    formData.append("content", content.trim());
    formData.append("isPublished", String(isPublished));

    if (selectedFile) {
      formData.append("coverImage", selectedFile);
    }

    try {
      await updateBlogPost({ id, formData }).unwrap();
      setSuccess(true);
      setTimeout(() => navigate("/admin/blogs"), 1500); // Route back to central list desk after short delay
    } catch (err) {
      setError(
        err?.data?.message || "Failed to update blog post down the road."
      );
    }
  };

  if (!currentPost && !isSubmitting) {
    return (
      <div className="admin-blog-error">
        ⚠️ Locating original journal post coordinates...
      </div>
    );
  }

  if (isSubmitting) {
    return (
      <div className="admin-blog-loading">
        <FuelLoading text="Overwriting asset records inside cloud containers..." />
      </div>
    );
  }

  return (
    <div className="admin-blog-wrapper fuel-fade-in">
      <div className="admin-blog-header">
        <h2>Modify Journal Entry</h2>
        <p>
          Edit text lines, toggle visibility states, or swap out AWS S3 image
          banner nodes.
        </p>
      </div>

      <form className="blog-form-card" onSubmit={handleSubmit}>
        {success && (
          <div className="blog-admin-success">
            🎉 Article modifications saved successfully! Redirecting...
          </div>
        )}
        {error && <div className="blog-admin-error">⚠️ {error}</div>}

        <div className="blog-form-grid">
          <div className="form-text-inputs">
            <label>
              Post Title *
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <div className="form-row-split">
              <label>
                Category Tag
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="Road Trips">🗺️ Road Trips</option>
                  <option value="National Parks">🏞️ National Parks</option>
                  <option value="Gear Reviews">⚙️ Gear Reviews</option>
                  <option value="Small Car Life">🚗 Small Car Life</option>
                </select>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
                Make article entry visible to public feeds
              </label>
            </div>

            <label>
              Short Excerpt Summary *
              <input
                type="text"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
              />
            </label>

            <div className="quill-wrapper-box">
              <span className="field-label-text">Article Body Content *</span>
              <ReactQuill
                theme="snow"
                value={content}
                onChange={setContent}
                modules={quillModules}
              />
            </div>
          </div>

          <div className="form-image-handler">
            <span className="section-label-tag">Cover Photo Asset</span>
            <div className="image-uploader-box">
              {previewUrl ? (
                <div className="image-preview-frame">
                  <img src={previewUrl} alt="Cover asset" />
                  <button
                    type="button"
                    className="clear-img-btn"
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewUrl("");
                    }}
                  >
                    Change Photo ✕
                  </button>
                </div>
              ) : (
                <div className="empty-upload-slate">
                  <span className="icon">📷</span>
                  <p>
                    Drop file or click below to append high-fidelity cover
                    assets.
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    id="blog-file-upload"
                    onChange={handleFileChange}
                  />
                  <label
                    htmlFor="blog-file-upload"
                    className="file-trigger-btn"
                  >
                    Choose New File
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        <button type="submit" className="fuel-primary-btn publish-log-btn">
          💾 Save Post Changes & Synchronize
        </button>
      </form>
    </div>
  );
}
